import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth, authService } from "auth";
import { AuthFormField } from "components/auth-form/AuthForm";

import { setCardNumber, setUserProfile, setUserType } from "store/userSlice";
import { formatCardNumber } from "utils/card";
import { apiErrorMessage } from "utils/errors/parseApiError";

// Byte-identical to users_app's isEmail. A looser client test sends "a@b@c.d" on to the server,
// which fails it, falls through to the card lookup and answers the deliberately-indistinguishable
// "Invalid credentials" — reporting a typo'd address as a wrong password, and spending one of the
// three attempts per IP.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CARD_PREFIX = "656491746770";
const LAST_FOUR_RE = /^\d{4}$/;
const FULL_CARD_RE = /^\d{16}$/;

export interface UseLoginFormReturn {
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  handleLogin: () => Promise<void>;
  handleCloseError: () => void;
}

export function useLoginForm(): UseLoginFormReturn {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    signIn,
    loading: authLoading,
    error: authError,
    clearError,
  } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const passwordRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    clearError();
    setShowLoading(false);
    setShowError(false);
    setErrorMsg("");
  };

  const resetForm = () => {
    setUsername("");
    setPassword("");
  };

  const handleLogin = async () => {
    try {
      // /auth/sign-in accepts either, and only does the card->email lookup for a card number.
      const typedEmail = EMAIL_RE.test(username.trim());
      const raw = username.trim().replace(/\s/g, "");
      if (!typedEmail && !LAST_FOUR_RE.test(raw) && !FULL_CARD_RE.test(raw)) {
        openError("Please enter your card number or email address.");
        return;
      }
      const identifier = typedEmail
        ? username.trim()
        : LAST_FOUR_RE.test(raw)
          ? CARD_PREFIX + raw
          : raw;

      setLoadingMsg("Signing in…");
      setShowLoading(true);
      await signIn(identifier, password);

      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        setShowLoading(false);
        return;
        // throw new Error("Invalid credentials. Please check your details.");
      }

      dispatch(setUserType("user-need-password-change"));
      setLoadingMsg("Loading account…");
      const profile = await authService.getUserProfile(currentUser.uid);

      // Keep the PAN they actually typed. A legacy holder of two cards resolves to the first of
      // the pair, so preferring the profile here would open the other card's balance and QR.
      const signedInWith = typedEmail ? undefined : identifier;

      if (profile.mustChangePassword) {
        setShowLoading(false);
        navigate("/change-password", {
          state: { cardNumber: signedInWith ?? profile.cardNumber },
        });
        return;
      }

      dispatch(setUserType("user"));
      dispatch(setUserProfile(profile));
      setShowLoading(false);

      // Always dispatched, even empty: sign-out does not clear the persisted slice, so a
      // cardholder with no cards would otherwise inherit the previous account's card number.
      dispatch(setCardNumber(signedInWith ?? profile.cardNumber ?? ""));
      navigate("/user", {
        replace: true,
      });
    } catch (err: any) {
      resetForm();
      // setUserType ran before the profile load, so anything that throws after
      // it leaves a signed-in cardholder with a valid userType — which makes
      // GuestOnlyRoute redirect straight to /user and swallow this error,
      // landing them on a page with no card number. Roll it back so the failure
      // stays on the login page.
      dispatch(setUserType(undefined));
      openError(
        apiErrorMessage(err, "Invalid credentials. Please check your details."),
      );
    }
  };

  const handleUsernameBlur = () => {
    if (EMAIL_RE.test(username.trim())) return;
    const raw = username.trim().replace(/\s/g, "");
    if (LAST_FOUR_RE.test(raw)) {
      setUsername(formatCardNumber(CARD_PREFIX + raw));
    } else if (/^\d{16}$/.test(raw)) {
      setUsername(formatCardNumber(raw));
    }
  };

  const focusPassword = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") passwordRef.current?.focus();
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const fields: AuthFormField[] = [
    {
      name: "username",
      label: "Card Number or Email",
      value: username,
      onChange: setUsername,
      onKeyDown: focusPassword,
      onBlur: handleUsernameBlur,
      placeholder: "Card number, last 4 digits, or email",
      autoComplete: "username",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      value: password,
      onChange: setPassword,
      onKeyDown: submitOnEnter,
      placeholder: "Enter your password",
      autoComplete: "current-password",
      inputRef: passwordRef,
    },
  ];

  const isShowingLoading = showLoading || authLoading;
  const isShowingError = showError || Boolean(authError);
  const resolvedErrorMsg = showError ? errorMsg : (authError?.message ?? "");

  return {
    fields,
    submitRef,
    isSubmitDisabled: !username || !password,
    showLoading: isShowingLoading,
    loadingMsg,
    showError: isShowingError,
    errorMsg: resolvedErrorMsg,
    handleLogin,
    handleCloseError,
  };
}
